import { useCallback, useEffect, useRef, useState } from 'react';
import { fxId } from './sportsnewListPatch';
import { reconcileDetailMarketsFromRestSnapshot } from './sportsnewDetailRestReconcile';
import { applySportsnewDetailRealtimeSince } from './sportsnewDetailRealtime';
import { createEvictionScheduler } from './evictionScheduler';
import {
  cancelLiveAllLockEvictions,
  findReplacementOdd,
  isEntityLocked,
  isMarketFullyLocked,
  marketLifecycleIdentity,
  marketOdds,
  remainingHideDelayMs,
  resolveReplacementHideDelayMs,
  shouldEvictFixtureForAvailability,
  shouldPreserveLiveAllLockedDetail,
} from './sportsnewDisplayLifecycle';

const enqueueLifecycleCommit = (commit) => {
  if (typeof queueMicrotask === 'function') queueMicrotask(commit);
  else Promise.resolve().then(commit);
};

export const useSportsnewDisplayLifecycle = ({
  detailFixture,
  detailFixtureId,
  detailRealtimeLedgerRef,
  detailMarkets,
  displayScope,
  fixtures,
  fixturesRef,
  mode,
  page,
  pageSize,
  selectedGameTitleId,
  selectedLeagueId,
  selectedSport,
  selectedTournamentId,
  selections,
}) => {
  /* ── Display lifecycle: soft-deleted (locked-then-evicted) odds/markets/
     fixtures. The underlying canonical data (fixtures/detailMarkets state)
     is NEVER purged here — only these id sets say "don't render this right
     now". A WS patch that reopens an id removes it from the relevant set
     instantly, at any point, even mid-countdown. See evictionScheduler.js /
     sportsnewDisplayLifecycle.js for the timer + decision logic. ── */
  const [hiddenOddIds, setHiddenOddIds] = useState(() => new Set());
  const [hiddenMarketIds, setHiddenMarketIds] = useState(() => new Set());
  const [hiddenFixtureIds, setHiddenFixtureIds] = useState(() => new Set());
  const [operatorSuspendedFixtureIds, setOperatorSuspendedFixtureIdsState] = useState(() => new Set());
  const operatorSuspendedFixtureIdsRef = useRef(operatorSuspendedFixtureIds);
  const setOperatorSuspendedFixtureIds = useCallback((update) => {
    const next = typeof update === 'function' ? update(operatorSuspendedFixtureIdsRef.current) : update;
    operatorSuspendedFixtureIdsRef.current = next;
    setOperatorSuspendedFixtureIdsState(next);
  }, []);
  const hiddenOddIdsRef = useRef(hiddenOddIds);
  const hiddenMarketIdsRef = useRef(hiddenMarketIds);
  const hiddenFixtureIdsRef = useRef(hiddenFixtureIds);
  useEffect(() => { hiddenOddIdsRef.current = hiddenOddIds; }, [hiddenOddIds]);
  useEffect(() => { hiddenMarketIdsRef.current = hiddenMarketIds; }, [hiddenMarketIds]);
  useEffect(() => { hiddenFixtureIdsRef.current = hiddenFixtureIds; }, [hiddenFixtureIds]);

  const updateOperatorSuspendedFixtureIds = useCallback((fixtureIds, suspended) => {
    if (!Array.isArray(fixtureIds) || fixtureIds.length === 0) return;
    setOperatorSuspendedFixtureIds((prev) => {
      const next = new Set(prev);
      fixtureIds.forEach((id) => {
        const normalized = String(id);
        if (suspended) next.add(normalized);
        else next.delete(normalized);
      });
      return next;
    });
  }, [setOperatorSuspendedFixtureIds]);

  // One eviction-timer namespace per entity level — never recreated across
  // renders, cancelled in full on unmount so no timer fires after teardown.
  const [oddEvictionScheduler] = useState(createEvictionScheduler);
  const [marketEvictionScheduler] = useState(createEvictionScheduler);
  const [fixtureEvictionScheduler] = useState(createEvictionScheduler);
  useEffect(() => () => {
    oddEvictionScheduler.cancelAll();
    marketEvictionScheduler.cancelAll();
    fixtureEvictionScheduler.cancelAll();
  }, [fixtureEvictionScheduler, marketEvictionScheduler, oddEvictionScheduler]);

  /* ── Stable refs so socket callbacks can read current state without stale closures ── */
  const detailMarketsRef = useRef([]);
  const selectedSportRef = useRef(selectedSport);
  const selectedLeagueIdRef = useRef(null);
  const selectedGameTitleIdRef = useRef(null);
  const selectedTournamentIdRef = useRef(null);
  const selectionsRef = useRef([]);
  const modeRef = useRef(mode);
  const displayScopeRef = useRef(displayScope);
  const pageRef = useRef(page);
  const pageSizeRef = useRef(pageSize);
  useEffect(() => { selectedSportRef.current = selectedSport; }, [selectedSport]);
  // Read via ref (not a handleListPatch dependency) so changing the league filter
  // doesn't recreate the callback and force a full socket reconnect.
  useEffect(() => { selectedLeagueIdRef.current = selectedLeagueId; }, [selectedLeagueId]);
  useEffect(() => { selectedGameTitleIdRef.current = selectedGameTitleId; }, [selectedGameTitleId]);
  useEffect(() => { selectedTournamentIdRef.current = selectedTournamentId; }, [selectedTournamentId]);
  // Same reasoning — handleSelectedPatch needs the current cart to compute a flash
  // direction when the backend omits previousOdds, without reconnecting on every
  // cart change.
  useEffect(() => { selectionsRef.current = selections; }, [selections]);
  // Same reasoning — handleListPatch needs to know the CURRENT mode to reject
  // stale patches from a room we've since left (see the guard inside it).
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { displayScopeRef.current = displayScope; }, [displayScope]);
  // Same reasoning — handleListPatch's recovery call needs the CURRENT
  // page/limit. Previously `page` was a direct useCallback dependency of
  // handleListPatch, which itself is a dependency of the socket-connection
  // effect — so every pagination click tore down and reopened the ENTIRE
  // WebSocket connection (all rooms unsubscribed and re-subscribed from
  // scratch), which is exactly the "connection keeps dropping" symptom.
  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { pageSizeRef.current = pageSize; }, [pageSize]);

  const reconcileRestDetailMarkets = useCallback((markets, fixture = null, realtimeRevisionAtRequest = null, marketsPage = null, options = {}) => (
    reconcileDetailMarketsFromRestSnapshot(detailMarketsRef.current,
      applySportsnewDetailRealtimeSince(markets, detailRealtimeLedgerRef.current,
        realtimeRevisionAtRequest, detailMarketsRef.current, {requireWriterForUnlock:modeRef.current==='live',...options}), {
      fixture,
      snapshotComplete: marketsPage?.hasMore === false && Number(marketsPage?.offset ?? 0) === 0,
      hiddenOddIds: hiddenOddIdsRef.current,
      hiddenMarketIds: hiddenMarketIdsRef.current,
    })
  ), [detailRealtimeLedgerRef, hiddenMarketIdsRef, hiddenOddIdsRef, modeRef]);

  /* ─────────────────────────────────────────────────────
     DISPLAY LIFECYCLE — selection/market lock → grace period → evict.
     Runs on every detailMarkets change, regardless of source (initial REST
     load, list-patch mainOdds sync, fixture-patch, selected-patch) — one
     place implementing the whole spec instead of duplicating it per patch
     handler. Compares against the PREVIOUS snapshot so a fresh transition
     into "locked" starts a countdown, a still-locked market on repeat ticks
     doesn't restart it, and any reopen cancels it immediately.
  ───────────────────────────────────────────────────── */
  const prevDetailMarketsForLifecycleRef = useRef([]);
  const liveAllLockProtectionRef = useRef({
    fixtureId: null, mode: null, scope: null, active: false,
    oddIds: new Set(), marketIds: new Set(),
  });
  useEffect(() => {
    const prevMarkets = prevDetailMarketsForLifecycleRef.current;
    const mode = modeRef.current;
    const prevOddsById = new Map();
    prevMarkets.forEach((m) => marketOdds(m).forEach((o) => {
      if (o.oddStateId != null) prevOddsById.set(o.oddStateId, o);
    }));

    const toUnhideOdds = [];
    const toUnhideMarkets = [];
    const activeFixtureId = detailFixtureId == null ? null : String(detailFixtureId);
    const previousProtection = liveAllLockProtectionRef.current;
    if (previousProtection.fixtureId !== activeFixtureId
      || previousProtection.mode !== mode || previousProtection.scope !== displayScope) {
      // Only protection provenance resets: genuinely hidden old lines stay hidden.
      liveAllLockProtectionRef.current = {
        fixtureId: activeFixtureId, mode, scope: displayScope, active: false,
        oddIds: new Set(), marketIds: new Set(),
      };
    }
    const preserveLiveAllLock = shouldPreserveLiveAllLockedDetail({
      fixture: fxId(detailFixture) === activeFixtureId ? detailFixture : null,
      markets: detailMarkets,
      mode,
      operatorSuspended: activeFixtureId != null
        && operatorSuspendedFixtureIds.has(activeFixtureId),
    });
    if (preserveLiveAllLock) {
      const protectedIds = liveAllLockProtectionRef.current;
      const currentIds = cancelLiveAllLockEvictions({
        markets: detailMarkets,
        oddScheduler: oddEvictionScheduler,
        marketScheduler: marketEvictionScheduler,
      });
      currentIds.oddIds.forEach((id) => {
        if (!hiddenOddIdsRef.current.has(id)) protectedIds.oddIds.add(id);
        else if (protectedIds.oddIds.has(id)) toUnhideOdds.push(id);
      });
      currentIds.marketIds.forEach((id) => {
        if (!hiddenMarketIdsRef.current.has(id)) protectedIds.marketIds.add(id);
        else if (protectedIds.marketIds.has(id)) toUnhideMarkets.push(id);
      });
      protectedIds.active = true;
    } else if (liveAllLockProtectionRef.current.active) {
      liveAllLockProtectionRef.current = {
        ...liveAllLockProtectionRef.current, active: false,
        oddIds: new Set(), marketIds: new Set(),
      };
    }

    if (!preserveLiveAllLock) detailMarkets.forEach((market, marketIndex) => {
      const marketId = marketLifecycleIdentity(market) ?? `idx:${marketIndex}`;
      let anyActiveChild = false;

      marketOdds(market).forEach((odd) => {
        const oddId = odd.oddStateId;
        if (oddId == null) return;
        const schedulerId = `odd:${oddId}`;
        const locked = isEntityLocked(odd);

        if (!locked) {
          anyActiveChild = true;
          oddEvictionScheduler.cancel(schedulerId);
          if (hiddenOddIdsRef.current.has(oddId)) toUnhideOdds.push(oddId);
          return;
        }

        const prevOdd = prevOddsById.get(oddId);
        const wasLocked = prevOdd ? isEntityLocked(prevOdd) : false;
        const replacement = findReplacementOdd(odd, market);
        const wasPending = oddEvictionScheduler.isPending(schedulerId);

        if (!wasLocked || !wasPending) {
          // Fresh transition into locked (or an untracked lock, e.g. right
          // after a reconnect) — start the countdown, honoring how long it
          // may already have been locked (lockedSince) so a recovered
          // session doesn't grant a full fresh grace period for free.
          const delay = replacement
            ? resolveReplacementHideDelayMs(odd, mode)
            : remainingHideDelayMs(odd, mode);
          oddEvictionScheduler.schedule(schedulerId, delay, () => {
            setHiddenOddIds((prev) => new Set(prev).add(oddId));
          });
        } else if (replacement) {
          // Was already counting down on the standard delay, but a
          // replacement line just appeared — shorten it.
          oddEvictionScheduler.schedule(schedulerId, resolveReplacementHideDelayMs(odd, mode), () => {
            setHiddenOddIds((prev) => new Set(prev).add(oddId));
          });
        }
        // else: still locked, already counting down, no new replacement — leave the timer alone.
      });

      const marketSchedulerId = `market:${marketId}`;
      if (anyActiveChild) {
        marketEvictionScheduler.cancel(marketSchedulerId);
        if (hiddenMarketIdsRef.current.has(marketId)) toUnhideMarkets.push(marketId);
      } else if (isMarketFullyLocked(market) && !marketEvictionScheduler.isPending(marketSchedulerId)) {
        marketEvictionScheduler.schedule(marketSchedulerId, remainingHideDelayMs(market, mode), () => {
          setHiddenMarketIds((prev) => new Set(prev).add(marketId));
        });
      }
    });

    if (toUnhideOdds.length > 0) {
      enqueueLifecycleCommit(() => {
        setHiddenOddIds((prev) => {
          const next = new Set(prev);
          toUnhideOdds.forEach((id) => next.delete(id));
          return next;
        });
      });
    }
    if (toUnhideMarkets.length > 0) {
      enqueueLifecycleCommit(() => {
        setHiddenMarketIds((prev) => {
          const next = new Set(prev);
          toUnhideMarkets.forEach((id) => next.delete(id));
          return next;
        });
      });
    }

    prevDetailMarketsForLifecycleRef.current = detailMarkets;
  }, [detailMarkets, detailFixture, detailFixtureId, displayScope, mode,
    operatorSuspendedFixtureIds, marketEvictionScheduler, oddEvictionScheduler]);

  /* ── Availability-only eviction is prematch-only. LIVE membership follows
     the server lifecycle/operator contract while locked selections stay disabled. ── */
  const prevFixturesForLifecycleRef = useRef([]);
  useEffect(() => {
    const prevById = new Map(prevFixturesForLifecycleRef.current.map((f) => [fxId(f), f]));
    const mode = modeRef.current;
    const toUnhide = [];

    fixtures.forEach((f) => {
      const id = fxId(f);
      if (id == null) return;
      const schedulerId = `fx:${id}`;
      const locked = shouldEvictFixtureForAvailability(f, mode);

      if (!locked) {
        fixtureEvictionScheduler.cancel(schedulerId);
        if (hiddenFixtureIdsRef.current.has(id)) toUnhide.push(id);
        return;
      }

      const prevF = prevById.get(id);
      const wasLocked = prevF ? shouldEvictFixtureForAvailability(prevF, mode) : false;
      if (!wasLocked || !fixtureEvictionScheduler.isPending(schedulerId)) {
        fixtureEvictionScheduler.schedule(schedulerId, remainingHideDelayMs(f.marketState ?? f, mode), () => {
          setHiddenFixtureIds((prev) => new Set(prev).add(id));
        });
      }
    });

    if (toUnhide.length > 0) {
      enqueueLifecycleCommit(() => {
        setHiddenFixtureIds((prev) => {
          const next = new Set(prev);
          toUnhide.forEach((id) => next.delete(id));
          return next;
        });
      });
    }

    prevFixturesForLifecycleRef.current = fixtures;
  }, [fixtureEvictionScheduler, fixtures, mode]);

  return {
    detailMarketsRef,
    displayScopeRef,
    fixturesRef,
    hiddenFixtureIds,
    hiddenMarketIds,
    hiddenOddIds,
    modeRef,
    operatorSuspendedFixtureIds,
    operatorSuspendedFixtureIdsRef,
    setOperatorSuspendedFixtureIds,
    pageRef,
    pageSizeRef,
    reconcileRestDetailMarkets,
    selectedGameTitleIdRef,
    selectedLeagueIdRef,
    selectedSportRef,
    selectedTournamentIdRef,
    selectionsRef,
    updateOperatorSuspendedFixtureIds,
  };
};
